/** Resolve currency_id for isolated margin base/quote asset. */
export function resolveMarginCurrencyId(pair, assetType, currencyData = []) {
  if (!pair) return null;
  const sym = assetType === "quote" ? pair.quote_asset : pair.base_asset;
  if (!sym) return null;
  const upper = String(sym).toUpperCase();
  const hit = (currencyData || []).find(
    (c) => String(c.short_name || c.currency || "").toUpperCase() === upper
  );
  return hit?.currency_id ?? null;
}

export function isMarginWallet(type) {
  return String(type || "").toLowerCase() === "margin";
}

export function isOptionsWallet(type) {
  return String(type || "").toLowerCase() === "options";
}

export function isFuturesWallet(type) {
  return String(type || "").toLowerCase() === "futures";
}

export function isOptionsInvolved(fromWallet, toWallet) {
  return isOptionsWallet(fromWallet) || isOptionsWallet(toWallet);
}

export function isFuturesInvolved(fromWallet, toWallet) {
  return isFuturesWallet(fromWallet) || isFuturesWallet(toWallet);
}

/** Options and futures wallet transfers are USDT-only. */
export function isUsdtOnlyTransferInvolved(fromWallet, toWallet) {
  return isOptionsInvolved(fromWallet, toWallet) || isFuturesInvolved(fromWallet, toWallet);
}

export function isMarginInvolved(fromWallet, toWallet) {
  return isMarginWallet(fromWallet) || isMarginWallet(toWallet);
}

export function findUsdtCurrency(currencyData = []) {
  return (
    (currencyData || []).find(
      (c) => String(c.short_name || c.currency || "").toUpperCase() === "USDT"
    ) ?? null
  );
}

/** Options / futures wallet transfers are USDT-only. */
export function filterTransferCurrencies(currencyData, fromWallet, toWallet) {
  if (!isUsdtOnlyTransferInvolved(fromWallet, toWallet)) return currencyData || [];
  const usdt = findUsdtCurrency(currencyData);
  return usdt ? [usdt] : [];
}

/** Build GET transfer-balance params per unified API rules. */
export function buildTransferBalanceParams({
  fromWalletType,
  toWalletType,
  currencyId,
  selectedMarginPair,
  marginAssetType,
}) {
  if (!fromWalletType || !toWalletType || !currencyId) return null;
  const params = {
    fromWallet: fromWalletType,
    toWallet: toWalletType,
    currencyId,
  };
  if (isMarginWallet(fromWalletType) && selectedMarginPair?.pair_id) {
    params.pairId = selectedMarginPair.pair_id;
    params.assetType = marginAssetType || "base";
  }
  return params;
}

/** Build POST /v1/wallet/transfer body. */
export function buildTransferParams({
  fromWalletType,
  toWalletType,
  currencyId,
  amount,
  selectedMarginPair,
  marginAssetType,
}) {
  if (!fromWalletType || !toWalletType || !currencyId || !amount) return null;
  const params = {
    fromWallet: fromWalletType,
    toWallet: toWalletType,
    currencyId,
    amount,
  };
  if (isMarginInvolved(fromWalletType, toWalletType) && selectedMarginPair?.pair_id) {
    params.pairId = selectedMarginPair.pair_id;
    params.assetType = marginAssetType || "base";
  }
  return params;
}

/** Optional hint text from transfer-balance `source` block. */
export function transferSourceHint(source) {
  if (!source || typeof source !== "object") return null;
  const reserved = parseFloat(source.isolated_reserved);
  if (Number.isFinite(reserved) && reserved > 0) {
    return `${reserved} ${source.asset || "USDT"} is reserved for open positions`;
  }
  const borrowed = parseFloat(source.borrowed);
  if (Number.isFinite(borrowed) && borrowed > 0) {
    return "Withdrawal limited by outstanding loan";
  }
  return null;
}

export function fiveDecimalTransfer(data) {
  if (typeof data === "number") {
    if (data <= 0) return "0.00";
    return parseFloat(data.toFixed(8));
  }
  return "N/A";
}

const TRANSFER_AMOUNT_EPSILON = 1e-8;

/** Inline validation for transfer amount vs available transferable balance. */
export function getTransferAmountError(transferAmount, transferable, { balanceLoading = false } = {}) {
  if (balanceLoading) return "";
  if (transferAmount === "" || transferAmount == null) return "";
  const amt = Number(transferAmount);
  if (!Number.isFinite(amt) || amt <= 0) return "";
  const max = parseFloat(transferable || 0);
  if (!Number.isFinite(max)) return "";
  if (amt > max + TRANSFER_AMOUNT_EPSILON) return "Insufficient balance";
  return "";
}
